import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import {
    isJsonContentType,
    isNewRelease,
    isSnoozeExpired,
    parseRemoteRelease,
    shouldShowUpdateNotice,
} from '../helpers/frontendRelease';
import {
    getRefreshTokenExpiresAtMs,
    isRefreshTokenExpiringWithin,
    RELOAD_BEFORE_TOKEN_EXPIRY_MS,
    reloadApp,
    scheduleReloadBeforeRefreshTokenExpiry,
} from '../helpers/frontendUpdateReload';
import { staffImportTokens } from '../styles/staffImportTokens';

declare const __FRONTEND_RELEASE_ID__: string;

const DEFAULT_POLL_INTERVAL_MS = 15 * 60 * 1000;
const DEFAULT_REMIND_LATER_MS = 2 * 60 * 60 * 1000;
const NOTICE_TEXT = 'Save your work and refresh';

export interface FrontendUpdateNoticeProps {
    currentRelease?: string;
    versionUrl?: string;
    pollIntervalMs?: number;
    remindLaterMs?: number;
    onRefresh?: () => void;
}

const Notice = styled.div`
    position: fixed;
    top: 16px;
    left: 50%;
    z-index: 1100;
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: calc(100vw - 32px);
    padding: 12px 16px;
    border: 1px solid #e6c85c;
    border-radius: 8px;
    background: ${staffImportTokens.colors.amber2};
    color: ${staffImportTokens.colors.textDefault};
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    font-weight: 500;
    transform: translateX(-50%);
`;

const ActionButtons = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
    padding: 6px 12px;
    border-radius: 4px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${staffImportTokens.colors.textPrimary};
        outline-offset: 2px;
    }

    ${({ $variant }) =>
        $variant === 'primary'
            ? css`
                  border: none;
                  background: ${staffImportTokens.colors.primary};
                  color: #ffffff;

                  &:hover {
                      opacity: 0.9;
                  }
              `
            : css`
                  border: 1px solid ${staffImportTokens.colors.textPrimary};
                  background: transparent;
                  color: ${staffImportTokens.colors.textPrimary};

                  &:hover {
                      background: rgba(0, 0, 0, 0.04);
                  }
              `}
`;

export const FrontendUpdateNotice: React.FC<FrontendUpdateNoticeProps> = ({
    currentRelease = __FRONTEND_RELEASE_ID__,
    versionUrl = '/staff-importer/version.json',
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    remindLaterMs = DEFAULT_REMIND_LATER_MS,
    onRefresh = reloadApp,
}) => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const snoozedUntilRef = useRef<number | null>(null);
    const updateDetectedRef = useRef(false);
    const snoozeTimeoutRef = useRef<number | null>(null);
    const cancelExpiryReloadRef = useRef<(() => void) | null>(null);
    const refreshTokenExpiresAtRef = useRef<number | null>(null);
    const stopPollingRef = useRef<(() => void) | null>(null);

    const clearSnoozeTimeout = useCallback(() => {
        if (snoozeTimeoutRef.current !== null) {
            window.clearTimeout(snoozeTimeoutRef.current);
            snoozeTimeoutRef.current = null;
        }
    }, []);

    const revealNoticeIfSnoozeExpired = useCallback(() => {
        if (!isSnoozeExpired(snoozedUntilRef.current)) {
            return;
        }

        snoozedUntilRef.current = null;
        setUpdateAvailable(true);
    }, []);

    const scheduleSnoozeWake = useCallback(() => {
        clearSnoozeTimeout();
        if (snoozedUntilRef.current === null) {
            return;
        }

        const delay = snoozedUntilRef.current - Date.now();
        if (delay <= 0) {
            revealNoticeIfSnoozeExpired();
            return;
        }

        snoozeTimeoutRef.current = window.setTimeout(() => {
            revealNoticeIfSnoozeExpired();
        }, delay);
    }, [clearSnoozeTimeout, revealNoticeIfSnoozeExpired]);

    const latchUpdateDetected = useCallback(() => {
        if (updateDetectedRef.current) {
            return;
        }

        updateDetectedRef.current = true;
        stopPollingRef.current?.();
        stopPollingRef.current = null;
        refreshTokenExpiresAtRef.current = getRefreshTokenExpiresAtMs();
        cancelExpiryReloadRef.current = scheduleReloadBeforeRefreshTokenExpiry(
            onRefresh,
            undefined,
            undefined,
            refreshTokenExpiresAtRef.current,
        );
    }, [onRefresh]);

    const checkForUpdate = useCallback(async () => {
        if (!currentRelease?.trim() || updateDetectedRef.current) {
            return;
        }

        try {
            const response = await fetch(versionUrl, { cache: 'no-store' });
            if (
                !response.ok ||
                !isJsonContentType(response.headers.get('content-type'))
            ) {
                return;
            }

            const remoteRelease = parseRemoteRelease(await response.json());
            if (!isNewRelease(currentRelease, remoteRelease)) {
                return;
            }

            latchUpdateDetected();
            setUpdateAvailable(
                shouldShowUpdateNotice({
                    currentRelease,
                    remoteRelease,
                    snoozedUntil: snoozedUntilRef.current,
                }),
            );
        } catch {
            // Version detection must never interrupt an active import.
        }
    }, [currentRelease, latchUpdateDetected, versionUrl]);

    useEffect(() => {
        if (!currentRelease?.trim()) {
            return undefined;
        }

        void checkForUpdate();

        const intervalId = window.setInterval(() => {
            void checkForUpdate();
        }, pollIntervalMs);
        stopPollingRef.current = () => {
            window.clearInterval(intervalId);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            if (!updateDetectedRef.current) {
                void checkForUpdate();
                return;
            }

            revealNoticeIfSnoozeExpired();

            const expiresAtMs = refreshTokenExpiresAtRef.current;
            if (
                expiresAtMs !== null &&
                isRefreshTokenExpiringWithin(
                    expiresAtMs,
                    RELOAD_BEFORE_TOKEN_EXPIRY_MS,
                )
            ) {
                cancelExpiryReloadRef.current?.();
                cancelExpiryReloadRef.current = null;
                onRefresh();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            stopPollingRef.current?.();
            stopPollingRef.current = null;
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
            cancelExpiryReloadRef.current?.();
            cancelExpiryReloadRef.current = null;
            clearSnoozeTimeout();
        };
    }, [
        checkForUpdate,
        clearSnoozeTimeout,
        currentRelease,
        onRefresh,
        pollIntervalMs,
        revealNoticeIfSnoozeExpired,
    ]);

    const handleRemindLater = () => {
        setUpdateAvailable(false);
        snoozedUntilRef.current = Date.now() + remindLaterMs;
        scheduleSnoozeWake();
    };

    if (!updateAvailable) {
        return null;
    }

    return (
        <Notice role="status" aria-live="polite">
            <span>{NOTICE_TEXT}</span>
            <ActionButtons>
                <ActionButton
                    type="button"
                    $variant="primary"
                    onClick={onRefresh}
                >
                    Refresh now
                </ActionButton>
                <ActionButton
                    type="button"
                    $variant="secondary"
                    onClick={handleRemindLater}
                >
                    Remind me later
                </ActionButton>
            </ActionButtons>
        </Notice>
    );
};

export default FrontendUpdateNotice;
